import React, { useEffect, useState } from "react";
import Icon from "@mdi/react";
import {
	mdiChartLine,
	mdiDotsVertical,
	mdiAccountGroup,
	mdiCalendarCheck,
	mdiClipboardCheck,
	mdiClockOutline,
	mdiArrowUp,
	mdiArrowDown,
	mdiMenu,
	mdiStethoscope,
	mdiHospitalBuilding,
	mdiShieldCheck,
} from "@mdi/js";
import {
	CircularProgress,
	IconButton,
} from "@mui/material";
import { MainLayout, useMobileLayout } from "layouts";
import { useAccount } from "shared/hooks";
import { MedDoctorServices, MedDashboardDTO } from "shared/services";
import styles from "./styles.module.scss";

// ── Main Component ────────────────────────────────────────────────

export const MedDashboardPage: React.FC = () => {
	const user = useAccount();
	const { isMobile, openDrawer } = useMobileLayout();
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState<MedDashboardDTO | null>(null);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				setLoading(true);
				const stats = await MedDoctorServices.getDashboardStats();
				if (!cancelled) setData(stats);
			} catch (err: any) {
				console.error("Dashboard error:", err);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, []);

	if (loading) {
		return (
			<MainLayout>
				<div className={styles.emptyState}>
					<CircularProgress size={32} />
					<p>Carregando dashboard…</p>
				</div>
			</MainLayout>
		);
	}

	const kpis = data?.kpis;
	const activity = data?.recentActivity || [];

	const kpiCards = [
		{ label: "Consultas Hoje", value: String(kpis?.appointmentsToday ?? 0), change: kpis?.appointmentsTodayChange ?? 0, icon: mdiCalendarCheck, color: "#003B71" },
		{ label: "Pacientes Ativos", value: String(kpis?.patientsTotal ?? 0), change: kpis?.patientsTotalChange ?? 0, icon: mdiAccountGroup, color: "#0077B6" },
		{ label: "Tempo Médio Consulta", value: kpis?.avgDuration ? `${kpis.avgDuration} min` : "—", change: kpis?.avgDurationChange ?? 0, icon: mdiClockOutline, color: "#0096A7" },
		{ label: "Adesão a Protocolos", value: kpis?.adherence ? `${kpis.adherence}%` : "—", change: kpis?.adherenceChange ?? 0, icon: mdiClipboardCheck, color: "#6C63FF" },
	];

	return (
		<MainLayout>
			<div className={styles.dashboardPage}>
				{/* Header */}
				<div className={styles.dashboardHeader}>
					<div>
						<h1>Dashboard Clínico</h1>
						<p>Visão geral de métricas e desempenho</p>
					</div>
					{isMobile && (
						<IconButton onClick={openDrawer}>
							<Icon path={mdiMenu} size={1} />
						</IconButton>
					)}
				</div>

				{/* KPI Cards */}
				<div className={styles.kpiGrid}>
					{kpiCards.map((kpi, i) => (
						<div key={i} className={styles.kpiCard}>
							<div className={styles.kpiIconBox} style={{ background: `${kpi.color}15`, color: kpi.color }}>
								<Icon path={kpi.icon} size={1.2} />
							</div>
							<div className={styles.kpiContent}>
								<span className={styles.kpiLabel}>{kpi.label}</span>
								<span className={styles.kpiValue}>{kpi.value}</span>
								<span className={styles.kpiChange} style={{ color: kpi.change >= 0 ? "#4CAF50" : "#f44336" }}>
									<Icon path={kpi.change >= 0 ? mdiArrowUp : mdiArrowDown} size={0.6} />
									{Math.abs(kpi.change)}% vs. mês anterior
								</span>
							</div>
						</div>
					))}
				</div>

				{/* Sections */}
				<div className={styles.dashboardSections}>
					{/* Recent Activity */}
					<div className={styles.dashSection} style={{ gridColumn: "1 / -1" }}>
						<div className={styles.dashSectionHeader}>
							<Icon path={mdiStethoscope} size={0.9} />
							<h2>Atividade Recente</h2>
						</div>
						<div className={styles.dashSectionBody}>
							{activity.length === 0 && (
								<p style={{ textAlign: "center", color: "var(--mui-palette-text-secondary)", padding: "1rem" }}>
									Nenhuma consulta agendada para hoje.
								</p>
							)}
							{activity.map((a) => (
								<div key={a.id} className={styles.activityRow}>
									<span className={styles.activityTime}>{a.time}</span>
									<div className={styles.activityInfo}>
										<span className={styles.activityPatient}>{a.patient}</span>
										<span className={styles.activityType}>{a.type}{a.specialty ? ` — ${a.specialty}` : ""}</span>
									</div>
									<span
										className={styles.activityStatus}
										data-status={a.status === "Concluída" ? "done" : a.status === "Em andamento" ? "active" : "pending"}
									>
										{a.status}
									</span>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
		</MainLayout>
	);
};
